import { z } from 'zod';
import { JsonObjectSchema } from './common.js';
import { ChatTraceSchema, ToolCallSchema } from './chat.js';
// =============================================================================
// Admin chat runs
// =============================================================================
//
// Two read-only admin routes over ChatTrace/LlmModelCall/LlmToolCall: a single
// run's detail, and "what the model saw" for one model call. See
// `apps/backend/platform/src/domains/chat/chat.service.runs.ts`.
export const ChatRunDetailParamsSchema = z.object({
    traceId: z.uuid(),
});
const LlmModelCallSchema = z.object({
    id: z.string(),
    step: z.number(),
    attempt: z.number(),
    modelName: z.string(),
    status: z.enum(['success', 'error']),
    errorClass: z.string().nullable(),
    error: z.string().nullable(),
    finishReason: z.string().nullable(),
    unusableReason: z.string().nullable(),
    startedAt: z.string(),
    firstTokenAt: z.string().nullable(),
    endedAt: z.string(),
    promptTokens: z.number(),
    completionTokens: z.number(),
    cachedTokens: z.number(),
    /** Null on a call recorded before cache writes were captured; priced as 0 writes. */
    cacheWriteTokens: z.number().nullable(),
    messageCount: z.number(),
    toolCount: z.number(),
    loadedToolsets: z.array(z.string()),
    estimatedMessageTokens: z.number(),
    /**
     * Estimated tokens of the tool declarations bound on this call (`selectSurface`); null on a call
     * that predates it. Optional so an admin bundle built against this schema still reads a backend
     * deployed before the field existed.
     */
    boundDeclarationTokens: z.number().nullable().optional(),
    /** Tool calls in the response to a name that was not bound on this call; null on a call that predates it, absent from an older backend. */
    unboundToolCalls: z.number().nullable().optional(),
    /** Names of those calls, one per call; null when the call recorded none or predates it, absent from an older backend. */
    unboundToolNames: z.array(z.string()).nullable().optional(),
    /** Integer micro-USD. Null when the model has no price entry at `startedAt`. */
    costMicroUsd: z.number().nullable(),
    /**
     * The suggestions a `suggestReplies` call that ended the turn carried, exactly as the model wrote
     * them (before the screen); null when the call did not end on one. The call is not on the stored
     * reply, so this is its only record. Optional for an older backend.
     */
    suggestReplies: z.object({ suggestions: z.array(z.unknown()) }).nullable().optional(),
    /**
     * The arguments of an `offerConnectCard` call that ended the turn, exactly as the model wrote them
     * (before the card was screened); null when the call did not end on one. The call is not on the
     * stored reply, so this is its only record. Optional for an older backend.
     */
    offerConnectCard: z.object({ args: z.unknown() }).nullable().optional(),
    /** The same record for an `offerPhoneCard` call that ended the turn. Optional for an older backend. */
    offerPhoneCard: z.object({ args: z.unknown() }).nullable().optional(),
});
const LlmToolCallSchema = z.object({
    id: z.string(),
    step: z.number(),
    toolCallId: z.string(),
    toolName: z.string(),
    status: z.enum(['success', 'error']),
    startedAt: z.string(),
    endedAt: z.string(),
});
const ChatRunMessageSchema = z.object({
    id: z.string(),
    role: z.enum(['human', 'ai', 'tool', 'system', 'thinking']),
    content: z.string(),
    /** Set on an `ai` row that requested one or more tool calls; null otherwise.
     *  A tool call's args live here rather than on the `LlmToolCall` span, which
     *  holds no conversation content. */
    toolCalls: z.array(ToolCallSchema).nullable(),
    toolCallId: z.string().nullable(),
    toolName: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * How a routed run's tools were chosen, counts only. `fallback` means the router gave no picks and the
 * run got the caller's core; `reason` says why.
 */
export const ToolRoutingSummarySchema = z.object({
    outcome: z.enum(['routed', 'fallback']),
    reason: z.enum(['timeout', 'rate_limited', 'http_error', 'malformed', 'network', 'unavailable', 'no-turn', 'switched-off']).optional(),
    latencyMs: z.number(),
    addedCount: z.number(),
    missCount: z.number(),
});
export const ChatRunDetailSchema = z.object({
    trace: ChatTraceSchema,
    modelCalls: z.array(LlmModelCallSchema),
    toolCalls: z.array(LlmToolCallSchema),
    messages: z.array(ChatRunMessageSchema),
    /** Null on a run that was not routed and on a run recorded before routing was stored; optional for an older backend. */
    toolRouting: ToolRoutingSummarySchema.nullable().optional(),
});
export const ModelCallInputParamsSchema = z.object({
    traceId: z.uuid(),
    modelCallId: z.uuid(),
});
const ModelCallInputMessageSchema = z.object({
    role: z.enum(['system', 'human', 'ai', 'tool']),
    content: z.string(),
    toolCalls: z.array(z.object({ id: z.string().optional(), name: z.string(), args: JsonObjectSchema })).nullable(),
    toolCallId: z.string().nullable(),
    name: z.string().nullable(),
    /** Row ids this rendered message was built from. Empty for the prepended system
     *  message. More than one when consecutive tool results were merged into a
     *  single function-response turn (see `mergeConsecutiveToolMessages`). */
    sourceMessageIds: z.array(z.string()),
    /** True when at least one source row was rendered as the elided placeholder. */
    aged: z.boolean(),
    /** Sent to the model on this call but never stored (a rejected retry reply, its
     *  correction). Always ordered after the merged, stored messages. */
    ephemeral: z.boolean(),
});
export const ModelCallInputSchema = z.object({
    boundTools: z.array(z.string()),
    messages: z.array(ModelCallInputMessageSchema),
});
